import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/pages/home/components/Sidebar";
import CogNetGraphCanvas, {
  COGNET_COMPARE_EDGES,
  COGNET_COMPARE_LEAF_NODES,
  COGNET_COMPARE_MAIN_NODES,
  COGNET_EDGES,
  COGNET_LEAF_NODES,
  COGNET_MAIN_NODES,
  COGNET_NODE_DOMAIN_MAP,
  CogNetGraphCanvasRef,
} from "./components/CogNetGraphCanvas";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

type ActivePanel = "timeline" | "updates" | "compare" | "compare-records" | null;

const domainColorMap: Record<string, string> = Object.fromEntries(
  COGNET_MAIN_NODES.map((n) => [COGNET_NODE_DOMAIN_MAP[n.id] ?? n.label, n.color]),
) as Record<string, string>;

type NodeSource =
  | { kind: "qa"; title: string; href: string }
  | { kind: "kb"; title: string; href: string };

type NodeDetail = {
  title: string;
  meaning: string;
  sources: NodeSource[];
};

type NodeDetailComputed = NodeDetail & {
  nodeId: string;
  nodeKind: "main" | "leaf" | "unknown";
  parentId?: string;
  parentTitle?: string;
  aggregatedSources?: NodeSource[];
};

const DEFAULT_NODE_DETAIL: NodeDetail = {
  title: "节点详情",
  meaning: "该节点来自你的学习/研究过程沉淀，点击可查看与其相关的知识连接与来源记录。",
  sources: [
    { kind: "qa", title: "科学对话：相关问答记录", href: "/home" },
    { kind: "kb", title: "知识库：相关资料入口", href: "/knowledge" },
  ],
};

const NODE_DETAIL_MAP: Record<string, NodeDetail> = {
  relativity: {
    title: "相对论",
    meaning:
      "用于组织时空、引力与参考系变换的核心框架。你在阅读综述与课程笔记时多次引用它来解释宏观现象与现代物理的基础假设。",
    sources: [
      { kind: "qa", title: "科学对话：如何用相对论解释引力与时空弯曲？", href: "/home?topic=relativity" },
      { kind: "kb", title: "知识库：相对论综述与笔记", href: "/knowledge" },
    ],
  },
  quantum: {
    title: "量子力学",
    meaning:
      "描述微观体系状态与测量的理论基础。你的节点来源主要来自量子基础/量子信息的问答与文献导入，常与统计、数学工具形成连接。",
    sources: [
      { kind: "qa", title: "科学对话：学术综述里如何组织量子基础与实验进展？", href: "/home?topic=quantum-review" },
      { kind: "kb", title: "知识库导入：量子相关论文与笔记", href: "/knowledge/import?method=file" },
    ],
  },
  statistical: {
    title: "统计力学",
    meaning:
      "把微观自由度的统计规律映射到宏观热力学量，是你做模型假设、误差分析与实验解释的常用支撑节点。",
    sources: [
      { kind: "qa", title: "科学对话：统计力学与热力学涨落怎么写进综述？", href: "/home?topic=statmech" },
      { kind: "kb", title: "知识库：统计力学课程/笔记导入", href: "/knowledge" },
    ],
  },
  scientific: {
    title: "科学方法",
    meaning:
      "用于沉淀你的研究流程与验证范式：提出假设、设计实验、构建对照、复现与复核。它常作为跨学科节点连接到实验设计与论文写作。",
    sources: [
      { kind: "qa", title: "科学对话：学术综述如何写方法与证据链？", href: "/home?topic=methodology" },
      { kind: "kb", title: "知识库：实验记录/方法论导入", href: "/knowledge/import?method=chat" },
    ],
  },
  photoelectric: {
    title: "光电效应",
    meaning:
      "典型的量子现象，用于连接“光的粒子性/能量量子化”与实验观测。你在做学术综述问题拆解时把它作为示例节点引用。",
    sources: [
      { kind: "qa", title: "科学对话：关于学术综述结构（以光电效应为例）", href: "/home?topic=review-structure" },
      { kind: "kb", title: "知识库导入：相关教材/讲义", href: "/knowledge/import?method=file" },
    ],
  },
};

// Leaf nodes represent "specific questions / facts". Their parent (main node) aggregates all leaf sources.
const LEAF_DETAIL_MAP: Record<string, NodeDetail> = {
  boltzmann: {
    title: "玻尔兹曼分布",
    meaning: "在统计力学中用于描述能量态占据概率，是你在写综述“模型假设与误差来源”时反复引用的具体知识点。",
    sources: [
      { kind: "qa", title: "科学对话：统计力学涨落怎么写进综述？", href: "/home?topic=statmech" },
      { kind: "kb", title: "知识库导入：统计力学课程笔记（PDF）", href: "/knowledge/import?method=file" },
    ],
  },
  partition: {
    title: "配分函数",
    meaning: "把微观态与宏观量连接起来的核心构造，用于推导自由能、熵与响应函数等。",
    sources: [
      { kind: "qa", title: "科学对话：配分函数和自由能的关系？", href: "/home?topic=partition" },
      { kind: "kb", title: "知识库：统计物理笔记条目", href: "/knowledge" },
    ],
  },
  thermo: {
    title: "热力学涨落",
    meaning: "用于解释实验测量中的随机性与误差界，帮助你把理论预测与实验现象对齐。",
    sources: [
      { kind: "qa", title: "科学对话：实验中噪声与涨落如何建模？", href: "/home?topic=fluctuation" },
      { kind: "kb", title: "知识库导入：实验记录与误差分析", href: "/knowledge/import?method=chat" },
    ],
  },
};

const uniqSources = (arr: NodeSource[]) => {
  const seen = new Set<string>();
  const out: NodeSource[] = [];
  for (const s of arr) {
    const key = `${s.kind}|${s.href}|${s.title}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

const timelineData = [
  {
    date: "2026-04-10",
    nodes: 305,
    edges: 138,
    label: "最新版本",
    newNodes: ["布朗运动", "黎曼几何", "拓扑学"],
    newEdges: ["统计力学 → 布朗运动", "数学 → 黎曼几何"],
    color: "#111827",
  },
  {
    date: "2026-03-28",
    nodes: 289,
    edges: 122,
    label: "",
    newNodes: ["宇宙论", "暗物质", "引力波"],
    newEdges: ["相对论 → 宇宙论", "物理学 → 引力波"],
    color: "#3B82F6",
  },
  {
    date: "2026-03-15",
    nodes: 261,
    edges: 103,
    label: "",
    newNodes: ["纠缠态", "量子相变", "超导体"],
    newEdges: ["量子力学 → 纠缠态", "统计力学 → 量子相变"],
    color: "#8B5CF6",
  },
  {
    date: "2026-02-22",
    nodes: 238,
    edges: 88,
    label: "",
    newNodes: ["费曼图", "路径积分", "场论"],
    newEdges: ["量子力学 → 费曼图", "数学 → 路径积分"],
    color: "#F97316",
  },
  {
    date: "2026-02-05",
    nodes: 204,
    edges: 71,
    label: "",
    newNodes: ["热力学", "熵增原理", "卡诺循环"],
    newEdges: ["统计力学 → 热力学", "物理学 → 熵增原理"],
    color: "#EAB308",
  },
  {
    date: "2026-01-18",
    nodes: 172,
    edges: 58,
    label: "初始版本",
    newNodes: ["相对论", "量子力学", "经典力学", "数学"],
    newEdges: ["物理学 → 相对论", "物理学 → 量子力学"],
    color: "#9CA3AF",
  },
];

const updateRecords = [
  { id: "u1", type: "add_node", icon: "ri-add-circle-line", color: "#111827", bg: "#F5F5F5", title: "添加节点：布朗运动", detail: "关联领域：统计力学、热力学、概率论", time: "2026-04-10 14:32" },
  { id: "u2", type: "add_edge", icon: "ri-links-line", color: "#111827", bg: "#F5F5F5", title: "建立关联：统计力学 → 布朗运动", detail: "关联强度：强关联 · 来源：文献导入", time: "2026-04-10 14:33" },
  { id: "u3", type: "add_node", icon: "ri-add-circle-line", color: "#111827", bg: "#F5F5F5", title: "添加节点：黎曼几何", detail: "关联领域：数学、相对论、微分几何", time: "2026-04-10 11:20" },
  { id: "u4", type: "edit_node", icon: "ri-edit-line", color: "#111827", bg: "#F5F5F5", title: "修改节点：量子纠错", detail: "更新摘要与关联概念，新增引用文献 3 篇", time: "2026-04-09 16:44" },
  { id: "u5", type: "remove_edge", icon: "ri-scissors-cut-line", color: "#111827", bg: "#F5F5F5", title: "移除关联：经典力学 → 布朗运动", detail: "重新评估后认为关联强度不足", time: "2026-04-09 09:11" },
  { id: "u6", type: "add_node", icon: "ri-add-circle-line", color: "#111827", bg: "#F5F5F5", title: "添加节点：拓扑学", detail: "关联领域：数学、量子场论、相对论", time: "2026-04-08 20:05" },
  { id: "u7", type: "add_edge", icon: "ri-links-line", color: "#111827", bg: "#F5F5F5", title: "建立关联：数学 → 拓扑学", detail: "关联强度：强关联 · 来源：手动添加", time: "2026-04-08 20:06" },
  { id: "u8", type: "merge_node", icon: "ri-merge-cells-vertical", color: "#111827", bg: "#F5F5F5", title: "合并节点：量子力学 · 量子理论", detail: "将「量子理论」合并入「量子力学」主节点", time: "2026-04-07 13:30" },
];

const compareRecordsList = [
  {
    id: "cr1",
    targetLink: "cognet.ai/u/physicist/305",
    myNodes: 305,
    otherNodes: 268,
    commonNodes: 214,
    myOnlyNodes: 91,
    otherOnlyNodes: 54,
    myEdges: 138,
    otherEdges: 116,
    commonEdges: 92,
    note: "对方图谱在宇宙学与拓扑方向更密集，你在量子与数学基础方向更完整。",
    time: "2026-04-11 10:22",
  },
  {
    id: "cr2",
    targetLink: "cognet.ai/u/mathlover/221",
    myNodes: 305,
    otherNodes: 221,
    commonNodes: 176,
    myOnlyNodes: 129,
    otherOnlyNodes: 45,
    myEdges: 138,
    otherEdges: 97,
    commonEdges: 74,
    note: "你更偏向物理-数学交叉，对方更偏向纯数学与方法论。",
    time: "2026-03-29 09:15",
  },
  {
    id: "cr3",
    targetLink: "cognet.ai/u/logiclab/190",
    myNodes: 305,
    otherNodes: 190,
    commonNodes: 158,
    myOnlyNodes: 147,
    otherOnlyNodes: 32,
    myEdges: 138,
    otherEdges: 82,
    commonEdges: 69,
    note: "对方在哲学和科学方法相关节点更集中，你的网络规模更大、跨域链接更多。",
    time: "2026-02-23 18:40",
  },
];

export default function CogNetPage() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const graphRef = useRef<CogNetGraphCanvasRef>(null);
  const graphWrapRef = useRef<HTMLDivElement>(null);
  const [showEdges, setShowEdges] = useState(true);
  const [smartLayout, setSmartLayout] = useState(true);
  const [searchVal, setSearchVal] = useState("");
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [domainFilter, setDomainFilter] = useState<string[]>([]);
  const [domainsCollapsed, setDomainsCollapsed] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [activeSnapshotDate, setActiveSnapshotDate] = useState<string>("2026-04-10");
  const [exporting, setExporting] = useState<null | "png" | "pdf">(null);
  const [darkMode, setDarkMode] = useState(false);

  // Compare panel state
  const [compareLink, setCompareLink] = useState("");
  const [compareResult, setCompareResult] = useState<null | typeof compareRecordsList[0]>(null);
  const [compareStarted, setCompareStarted] = useState(false);
  const [compareRecords, setCompareRecords] = useState(compareRecordsList);
  const [copied, setCopied] = useState(false);
  const compareGraphRef = useRef<CogNetGraphCanvasRef>(null);
  const [compareSelectedNode, setCompareSelectedNode] = useState<string | null>(null);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [compareDomainFilter, setCompareDomainFilter] = useState<string[]>([]);
  const [compareDomainsCollapsed, setCompareDomainsCollapsed] = useState(false);

  const resetCompareState = () => {
    setCompareStarted(false);
    setCompareResult(null);
    setCompareLink("");
    setCompareSelectedNode(null);
  };

  const selectedDetail = useMemo<NodeDetailComputed | null>(() => {
    if (!selectedNode) return null;
    const main = COGNET_MAIN_NODES.find((n) => n.id === selectedNode);
    const leaf = COGNET_LEAF_NODES.find((n) => n.id === selectedNode);

    // Leaf (specific question/fact)
    if (leaf) {
      // Leaf nodes should be tied to a single, precise provenance record by default.
      const base =
        LEAF_DETAIL_MAP[leaf.id] ??
        ({
          title: leaf.label,
          meaning: "这是一个更细粒度的子节点，用于记录你在某一次具体问题/材料导入中获得的知识点。",
          sources: [
            {
              kind: "qa",
              title: `科学对话：关于「${leaf.label}」的问答记录`,
              href: `/home?node=${encodeURIComponent(leaf.id)}`,
            },
          ],
        } satisfies NodeDetail);
      const parentMain = COGNET_MAIN_NODES.find((n) => n.id === leaf.parent);
      const parentBase = parentMain ? (NODE_DETAIL_MAP[parentMain.id] ?? { ...DEFAULT_NODE_DETAIL, title: parentMain.label }) : null;
      const siblings = COGNET_LEAF_NODES.filter((x) => x.parent === leaf.parent);
      const agg = parentMain
        ? uniqSources([
            ...(parentBase?.sources ?? []),
            ...siblings.flatMap((s) => (LEAF_DETAIL_MAP[s.id]?.sources ?? [])),
          ])
        : undefined;

      return {
        ...base,
        nodeId: leaf.id,
        nodeKind: "leaf",
        parentId: parentMain?.id,
        parentTitle: parentMain?.label,
        aggregatedSources: agg,
      };
    }

    // Main (parent) aggregates all leaf sources
    if (main) {
      const base = NODE_DETAIL_MAP[main.id] ?? { ...DEFAULT_NODE_DETAIL, title: main.label };
      const children = COGNET_LEAF_NODES.filter((x) => x.parent === main.id);
      const agg = uniqSources([...(base.sources ?? []), ...children.flatMap((c) => (LEAF_DETAIL_MAP[c.id]?.sources ?? []))]);
      return {
        ...base,
        nodeId: main.id,
        nodeKind: "main",
        aggregatedSources: agg,
      };
    }

    // Unknown
    return {
      ...DEFAULT_NODE_DETAIL,
      title: selectedNode,
      nodeId: selectedNode,
      nodeKind: "unknown",
      aggregatedSources: uniqSources(DEFAULT_NODE_DETAIL.sources),
    };
  }, [selectedNode]);

  const rightPanelOpen = !!activePanel || (detailOpen && !!selectedNode);

  const togglePanel = (panel: ActivePanel) => {
    setActivePanel((prev) => {
      if (prev === panel) {
        if (panel === "compare") resetCompareState();
        return null;
      }
      if (prev === "compare" || panel !== "compare") resetCompareState();
      return panel;
    });
    setShowShare(false);
  };


  const handleCompare = () => {
    const cleanLink = compareLink.trim();
    if (!cleanLink) return;
    setCompareStarted(true);
    const now = new Date().toLocaleString("zh-CN").slice(0, 16);
    const result = {
      id: `cr${Date.now()}`,
      targetLink: cleanLink,
      myNodes: computeVariant.my.nodes,
      otherNodes: computeVariant.other.nodes,
      commonNodes: computeVariant.commonLabels.length,
      myOnlyNodes: computeVariant.myOnlyLabels.length,
      otherOnlyNodes: computeVariant.otherOnlyLabels.length,
      myEdges: computeVariant.my.edges,
      otherEdges: computeVariant.other.edges,
      commonEdges: computeVariant.commonEdges,
      note: `分析结果：共同概念 ${computeVariant.commonLabels.length} 个；你缺少 ${computeVariant.otherOnlyLabels.length} 个，对方缺少 ${computeVariant.myOnlyLabels.length} 个。`,
      time: now,
    };
    setCompareResult(result);
  };

  const handleSaveCompare = () => {
    if (!compareResult) return;
    setCompareRecords((prev) => [compareResult, ...prev]);
    setActivePanel("compare-records");
  };

  const compareSummary = compareResult
    ? [
        { label: "我的节点", value: compareResult.myNodes },
        { label: "对方节点", value: compareResult.otherNodes },
        { label: "共同节点", value: compareResult.commonNodes },
        { label: "差异节点", value: compareResult.myOnlyNodes + compareResult.otherOnlyNodes },
      ]
    : [];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText("cognet.ai/u/physicist/305");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const exportMissing = (kind: "txt" | "csv") => {
    const list = computeVariant.otherOnlyLabels;
    const content =
      kind === "csv"
        ? ["name", ...list].join("\n")
        : ["我缺少的节点：", ...list.map((x) => `- ${x}`)].join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = kind === "csv" ? `CogNet_missing_${compareResult?.id ?? "latest"}.csv` : `CogNet_missing_${compareResult?.id ?? "latest"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const snapshotConfig = useMemo(() => {
    // Approximate "historical snapshots" by progressively revealing nodes.
    // Later you can replace this with real versioned data from backend.
    const mainAll = ["relativity", "photoelectric", "statistical", "quantum", "scientific", "cosmology", "philosophy", "math", "brownian"];
    const leafBase = ["equiv", "spacetime", "wavefunc", "quantumstate", "einsteinEq", "boltzmann", "partition", "thought", "cosmoconst", "linAlg", "diffusion", "uncertainty"];

    const byDate: Record<string, { main: string[]; leaf: string[] }> = {
      "2026-01-18": { main: ["relativity", "quantum", "math", "statistical"], leaf: ["equiv", "spacetime", "wavefunc", "linAlg"] },
      "2026-02-05": { main: ["relativity", "quantum", "math", "statistical", "philosophy"], leaf: ["equiv", "spacetime", "wavefunc", "quantumstate", "linAlg", "boltzmann"] },
      "2026-02-22": { main: ["relativity", "quantum", "math", "statistical", "philosophy", "photoelectric"], leaf: ["equiv", "spacetime", "wavefunc", "quantumstate", "uncertainty", "linAlg", "boltzmann", "partition", "einsteinEq"] },
      "2026-03-15": { main: ["relativity", "quantum", "math", "statistical", "philosophy", "photoelectric", "scientific"], leaf: [...leafBase] },
      "2026-03-28": { main: ["relativity", "quantum", "math", "statistical", "philosophy", "photoelectric", "scientific", "cosmology"], leaf: [...leafBase, "cosmoconst"] },
      "2026-04-10": { main: mainAll, leaf: leafBase },
    };
    return byDate[activeSnapshotDate] ?? byDate["2026-04-10"];
  }, [activeSnapshotDate]);

  const searchConfig = useMemo(() => {
    const q = searchVal.trim().toLowerCase();
    if (!q) return null;

    const mineMain = COGNET_MAIN_NODES.filter((n) => snapshotConfig.main.includes(n.id));
    const mineLeaf = COGNET_LEAF_NODES.filter((l) => snapshotConfig.leaf.length === 0 || snapshotConfig.leaf.includes(l.id));

    const mineMainHit = new Set(mineMain.filter((n) => n.label.toLowerCase().includes(q)).map((n) => n.id));
    const mineLeafHit = new Set(mineLeaf.filter((l) => l.label.toLowerCase().includes(q)).map((l) => l.id));
    const mineParentsFromLeaf = new Set(mineLeaf.filter((l) => mineLeafHit.has(l.id)).map((l) => l.parent));

    const mineVisibleMain = Array.from(new Set([...mineMainHit, ...mineParentsFromLeaf]));
    const mineVisibleLeaf = Array.from(mineLeafHit);

    // Compare side
    const otherMain = COGNET_COMPARE_MAIN_NODES;
    const otherLeaf = COGNET_COMPARE_LEAF_NODES;
    const otherMainHit = new Set(otherMain.filter((n) => n.label.toLowerCase().includes(q)).map((n) => n.id));
    const otherLeafHit = new Set(otherLeaf.filter((l) => l.label.toLowerCase().includes(q)).map((l) => l.id));
    const otherParentsFromLeaf = new Set(otherLeaf.filter((l) => otherLeafHit.has(l.id)).map((l) => l.parent));

    const otherVisibleMain = Array.from(new Set([...otherMainHit, ...otherParentsFromLeaf]));
    const otherVisibleLeaf = Array.from(otherLeafHit);

    return {
      q,
      mine: { main: mineVisibleMain, leaf: mineVisibleLeaf },
      other: { main: otherVisibleMain, leaf: otherVisibleLeaf },
    };
  }, [searchVal, snapshotConfig.main, snapshotConfig.leaf]);

  const computeVariant = useMemo(() => {
    const norm = (s: string) => s.trim();

    const applyDomain = (mainIds: string[]) => {
      if (domainFilter.length === 0) return mainIds;
      const domainSet = new Set(domainFilter);
      return mainIds.filter((id) => {
        const d = COGNET_NODE_DOMAIN_MAP[id];
        return d ? domainSet.has(d) : true;
      });
    };

    const myMainIds = applyDomain(snapshotConfig.main).filter((id) => (searchConfig ? searchConfig.mine.main.includes(id) : true));
    const myMainSet = new Set(myMainIds);
    const myLeaf = COGNET_LEAF_NODES.filter((l) => myMainSet.has(l.parent) && (snapshotConfig.leaf.length === 0 || snapshotConfig.leaf.includes(l.id)) && (searchConfig ? searchConfig.mine.leaf.includes(l.id) || searchConfig.mine.main.includes(l.parent) : true));

    const otherMainIds = applyDomain(COGNET_COMPARE_MAIN_NODES.map((n) => n.id)).filter((id) => (searchConfig ? searchConfig.other.main.includes(id) : true));
    const otherMainSet = new Set(otherMainIds);
    const otherLeaf = COGNET_COMPARE_LEAF_NODES.filter((l) => otherMainSet.has(l.parent) && (searchConfig ? searchConfig.other.leaf.includes(l.id) || searchConfig.other.main.includes(l.parent) : true));

    const myLabels = new Set<string>([
      ...COGNET_MAIN_NODES.filter((n) => myMainSet.has(n.id)).map((n) => norm(n.label)),
      ...myLeaf.map((l) => norm(l.label)),
    ]);
    const otherLabels = new Set<string>([
      ...COGNET_COMPARE_MAIN_NODES.filter((n) => otherMainSet.has(n.id)).map((n) => norm(n.label)),
      ...otherLeaf.map((l) => norm(l.label)),
    ]);

    const commonLabels = Array.from(myLabels).filter((x) => otherLabels.has(x)).sort((a, b) => a.localeCompare(b, "zh-CN"));

    const myOnlyLabels = Array.from(myLabels).filter((x) => !otherLabels.has(x)).sort((a, b) => a.localeCompare(b, "zh-CN"));
    const otherOnlyLabels = Array.from(otherLabels).filter((x) => !myLabels.has(x)).sort((a, b) => a.localeCompare(b, "zh-CN"));

    const labelOfMyMain = new Map(COGNET_MAIN_NODES.map((n) => [n.id, norm(n.label)]));
    const labelOfOtherMain = new Map(COGNET_COMPARE_MAIN_NODES.map((n) => [n.id, norm(n.label)]));

    const myEdgePairs = new Set<string>();
    for (const e of COGNET_EDGES) {
      if (!myMainSet.has(e.from) || !myMainSet.has(e.to)) continue;
      const a = labelOfMyMain.get(e.from);
      const b = labelOfMyMain.get(e.to);
      if (!a || !b) continue;
      myEdgePairs.add([a, b].sort().join("—"));
    }
    for (const l of myLeaf) {
      const p = labelOfMyMain.get(l.parent);
      if (!p) continue;
      myEdgePairs.add([p, norm(l.label)].sort().join("—"));
    }

    const otherEdgePairs = new Set<string>();
    for (const e of COGNET_COMPARE_EDGES) {
      if (!otherMainSet.has(e.from) || !otherMainSet.has(e.to)) continue;
      const a = labelOfOtherMain.get(e.from);
      const b = labelOfOtherMain.get(e.to);
      if (!a || !b) continue;
      otherEdgePairs.add([a, b].sort().join("—"));
    }
    for (const l of otherLeaf) {
      const p = labelOfOtherMain.get(l.parent);
      if (!p) continue;
      otherEdgePairs.add([p, norm(l.label)].sort().join("—"));
    }

    const commonEdges = Array.from(myEdgePairs).filter((x) => otherEdgePairs.has(x)).length;

    const myNodes = myLabels.size;
    const otherNodes = otherLabels.size;

    const myEdges = showEdges ? myEdgePairs.size : 0;
    const otherEdges = showEdges ? otherEdgePairs.size : 0;

    return {
      my: { mainIds: myMainIds, leafIds: myLeaf.map((l) => l.id), nodes: myNodes, edges: myEdges },
      other: { mainIds: otherMainIds, nodes: otherNodes, edges: otherEdges },
      commonLabels,
      commonColorMap: (() => {
        const map: Record<string, string> = {};
        for (const n of COGNET_MAIN_NODES) {
          if (myMainSet.has(n.id)) map[n.label.trim()] = n.color;
        }
        for (const l of myLeaf) {
          map[l.label.trim()] = l.pColor;
        }
        return map;
      })(),
      myOnlyLabels,
      otherOnlyLabels,
      commonEdges,
    };
  }, [domainFilter, showEdges, snapshotConfig.leaf, snapshotConfig.main, searchConfig]);

  const domainList = useMemo(() => {
    const visibleMainSet = new Set(computeVariant.my.mainIds);
    const visibleLeafSet = new Set(computeVariant.my.leafIds);

    const domainCounts = new Map<string, number>();

    // Count main nodes
    for (const id of computeVariant.my.mainIds) {
      const d = COGNET_NODE_DOMAIN_MAP[id];
      if (!d) continue;
      domainCounts.set(d, (domainCounts.get(d) ?? 0) + 1);
    }

    // Count leaf nodes under visible parents
    for (const l of COGNET_LEAF_NODES) {
      if (!visibleMainSet.has(l.parent)) continue;
      if (!visibleLeafSet.has(l.id)) continue;
      const d = COGNET_NODE_DOMAIN_MAP[l.parent];
      if (!d) continue;
      domainCounts.set(d, (domainCounts.get(d) ?? 0) + 1);
    }

    return Array.from(domainCounts.entries())
      .map(([name, count]) => ({ name, count, color: domainColorMap[name] ?? "#111827" }))
      .sort((a, b) => b.count - a.count);
  }, [computeVariant.my.mainIds, computeVariant.my.leafIds]);

  const compareDomainList = useMemo(() => {
    const counts = new Map<string, { count: number; color: string }>();
    for (const n of COGNET_COMPARE_MAIN_NODES) {
      const name = COGNET_NODE_DOMAIN_MAP[n.id] ?? n.label;
      const cur = counts.get(name);
      if (!cur) counts.set(name, { count: 1, color: n.color });
      else counts.set(name, { count: cur.count + 1, color: cur.color });
    }
    return Array.from(counts.entries())
      .map(([name, v]) => ({ name, count: v.count, color: v.color }))
      .sort((a, b) => b.count - a.count);
  }, []);

  const stats = useMemo(() => {
    const visibleMainSet = new Set(snapshotConfig.main);
    const visibleLeafSet = new Set(snapshotConfig.leaf);

    // apply domain filter to main nodes
    if (domainFilter.length > 0) {
      const domainSet = new Set(domainFilter);
      for (const id of Array.from(visibleMainSet)) {
        const d = COGNET_NODE_DOMAIN_MAP[id];
        if (!d || !domainSet.has(d)) visibleMainSet.delete(id);
      }
    }

    // leaf nodes: only those whose parent is visible + included by snapshot leaf set
    let leafCount = 0;
    for (const l of COGNET_LEAF_NODES) {
      if (!visibleMainSet.has(l.parent)) continue;
      if (visibleLeafSet.size > 0 && !visibleLeafSet.has(l.id)) continue;
      leafCount += 1;
    }

    const mainCount = visibleMainSet.size;
    const nodes = mainCount + leafCount;

    const mainEdges = COGNET_EDGES.filter((e) => visibleMainSet.has(e.from) && visibleMainSet.has(e.to)).length;
    const leafEdges = leafCount; // each visible leaf has exactly one parent edge
    const edges = showEdges ? mainEdges + leafEdges : 0;

    const domains = new Set<string>();
    for (const id of visibleMainSet) {
      const d = COGNET_NODE_DOMAIN_MAP[id];
      if (d) domains.add(d);
    }

    return { nodes, edges, domains: domains.size };
  }, [domainFilter, showEdges, snapshotConfig.main, snapshotConfig.leaf]);

  const exportGraph = async (kind: "png" | "pdf") => {
    const el = graphWrapRef.current;
    if (!el) return;
    setExporting(kind);
    try {
      const dataUrl = await toPng(el, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#FFFFFF",
      });

      if (kind === "png") {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `CogNet_${activeSnapshotDate}.png`;
        a.click();
      } else {
        const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const margin = 28;
        const imgW = pageW - margin * 2;
        const imgH = pageH - margin * 2;
        pdf.addImage(dataUrl, "PNG", margin, margin, imgW, imgH, undefined, "FAST");
        pdf.save(`CogNet_${activeSnapshotDate}.pdf`);
      }
    } finally {
      setExporting(null);
      setShowShare(false);
    }
  };

  const panelBtnClass = (panel: ActivePanel) =>
    `flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] cursor-pointer transition-colors whitespace-nowrap ${
      activePanel === panel
        ? "bg-[#111827] text-white font-medium"
        : "bg-white text-[#4B5563] border border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#111827]"
    }`;

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex-1 flex flex-col overflow-hidden bg-[#F7F8FA]">
        {/* Top header */}
        <div className="h-[72px] bg-white border-b border-[#E5E7EB] flex items-center px-6 gap-4 flex-shrink-0">
          {/* Left: back + profile */}
          <div className="flex items-center gap-3 min-w-0 shrink-0">
            <button
              onClick={() => navigate("/knowledge")}
              className="flex items-center gap-1.5 text-[13px] text-[#8B8FA8] hover:text-[#1D2129] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-[15px]"></i>
              </div>
              知识库
            </button>
            <span className="text-[#E5E6EB]">/</span>
            <div className="w-8 h-8 rounded-full bg-[#111827] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              P
            </div>
            <div>
              <p className="text-[13.5px] font-semibold text-[#1D2129] leading-tight">用户名特别长就缩略...</p>
              <p className="text-[11.5px] text-[#8B8FA8] mt-0.5">理论物理学家 · 305 节点</p>
            </div>
          </div>

          {/* Center: CogNet brand */}
          <div className="flex-1 flex items-center justify-center gap-2.5 min-w-0">
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-node-tree text-[18px] text-[#111827]"></i>
              </div>
              <span className="text-[16px] font-semibold text-[#111827] tracking-tight">CogNet</span>
            </div>
            <div className="bg-[#F5F5F5] border border-[#E5E7EB] text-[#111827] text-[11.5px] font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap">
              个人认知网络
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5">
            <button onClick={() => togglePanel("timeline")} className={panelBtnClass("timeline")}>
              <i className="ri-time-line text-[13px]"></i>时间轴
            </button>
            <button onClick={() => togglePanel("updates")} className={panelBtnClass("updates")}>
              <i className="ri-history-line text-[13px]"></i>更新记录
            </button>
            <button onClick={() => togglePanel("compare")} className={panelBtnClass("compare")}>
              <i className="ri-scales-3-line text-[13px]"></i>对比
            </button>
            <button onClick={() => togglePanel("compare-records")} className={panelBtnClass("compare-records")}>
              <i className="ri-file-list-3-line text-[13px]"></i>对比记录
            </button>

            {/* Share button */}
            <div className="relative ml-1">
              <button
                onClick={() => { setShowShare(!showShare); setActivePanel(null); }}
                className="flex items-center gap-1.5 text-[12.5px] font-medium text-white bg-[#111827] hover:bg-black px-4 py-1.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-share-forward-line text-[13px]"></i>
                分享
              </button>

              {/* Share popup */}
              {showShare && (
                <div
                  className="absolute right-0 top-full mt-2 w-[280px] bg-white border border-[#E5E6EB] rounded-2xl z-50 overflow-hidden"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
                >
                  <div className="px-4 pt-4 pb-3 border-b border-[#F0F1F4]">
                    <div className="flex items-center justify-between">
                      <span className="text-[13.5px] font-semibold text-[#1D2129]">分享认知网络</span>
                      <button onClick={() => setShowShare(false)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#9699A6] cursor-pointer">
                        <i className="ri-close-line text-[14px]"></i>
                      </button>
                    </div>
                    <p className="text-[12px] text-[#8B8FA8] mt-1">将你的认知图谱分享给他人</p>
                  </div>
                  <div className="p-4 flex flex-col gap-2.5">
                    {/* Link copy */}
                    <div className="flex items-center gap-2 bg-[#F5F6F9] rounded-xl px-3 py-2.5">
                      <span className="flex-1 text-[12px] text-[#646479] truncate">cognet.ai/u/physicist/305</span>
                      <button
                        onClick={handleCopyLink}
                        className="flex items-center gap-1 text-[12px] font-medium text-[#111827] hover:text-[#000000] cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-[12px]`}></i>
                        {copied ? "已复制" : "复制"}
                      </button>
                    </div>

                    {/* Share options */}
                    {[
                      { icon: "ri-image-line", color: "#111827", bg: "#F5F5F5", label: "导出为图片", desc: "保存高清网络图快照", onClick: () => exportGraph("png") },
                      { icon: "ri-file-pdf-line", color: "#111827", bg: "#F5F5F5", label: "导出为 PDF", desc: "保存为可分享的 PDF", onClick: () => exportGraph("pdf") },
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => opt.onClick?.()}
                        disabled={!!exporting}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F6F9] cursor-pointer text-left w-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0`} style={{ background: opt.bg }}>
                          <i className={`${opt.icon} text-[16px]`} style={{ color: opt.color }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-[#1D2129]">{opt.label}</p>
                          <p className="text-[11px] text-[#8B8FA8] mt-0.5">{opt.desc}</p>
                        </div>
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-s-line text-[14px] text-[#C2C4CC]"></i>
                        </div>
                      </button>
                    ))}

                    {/* Visibility toggle */}
                    <div className="flex items-center justify-between pt-2 border-t border-[#F0F1F4] mt-1">
                      <div>
                        <p className="text-[12.5px] font-medium text-[#1D2129]">允许他人引用节点</p>
                        <p className="text-[11px] text-[#8B8FA8]">其他用户可将你的节点关联到自己的图谱</p>
                      </div>
                      <button className="relative w-9 h-5 bg-[#111827] rounded-full cursor-pointer flex-shrink-0 ml-3">
                        <div className="absolute top-0.5 left-4 w-4 h-4 bg-white rounded-full shadow"></div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="h-[52px] bg-white border-b border-[#E5E7EB] flex items-center gap-8 px-5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-[#F5F5F5] rounded-lg">
              <i className="ri-node-tree text-[15px] text-[#111827]"></i>
            </div>
            <div>
              <div className="flex items-baseline gap-1.5 leading-tight">
                <span className="text-[15px] font-bold text-[#1D2129]">{stats.nodes}</span>
                <span className="text-[11px] text-[#8B8FA8]">节点</span>
              </div>
            </div>
          </div>
          <div className="w-px h-6 bg-[#E5E6EB]" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-[#F5F5F5] rounded-lg">
              <i className="ri-links-line text-[15px] text-[#111827]"></i>
            </div>
            <div>
              <div className="flex items-baseline gap-1.5 leading-tight">
                <span className="text-[15px] font-bold text-[#1D2129]">{stats.edges}</span>
                <span className="text-[11px] text-[#8B8FA8]">关联</span>
              </div>
            </div>
          </div>
          <div className="w-px h-6 bg-[#E5E6EB]" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-[#F5F5F5] rounded-lg">
              <i className="ri-stack-line text-[15px] text-[#111827]"></i>
            </div>
            <div>
              <div className="flex items-baseline gap-1.5 leading-tight">
                <span className="text-[15px] font-bold text-[#1D2129]">{stats.domains}</span>
                <span className="text-[11px] text-[#8B8FA8]">领域</span>
              </div>
            </div>
          </div>
          <div className="w-px h-6 bg-[#E5E6EB]" />
          <div className="flex items-center gap-1.5 text-[12.5px] text-[#6B7280]">
            <i className="ri-calendar-line text-[13px]"></i>
            <span>最后更新：{activeSnapshotDate}</span>
          </div>
          <button
            onClick={() => setDarkMode((v) => !v)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1D2129] hover:bg-[#F9FAFB]"
            title="切换深色模式"
          >
            <i className={`${darkMode ? "ri-moon-line" : "ri-sun-line"} text-[13px]`}></i>
            {darkMode ? "深色" : "浅色"}
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden p-8 gap-4 relative bg-[#F7F8FA]">
          {/* Graph area */}
          <div className="flex flex-1 min-w-0 h-full min-h-0 bg-white border border-white/70 rounded-3xl overflow-hidden shadow-[0_8px_24px_rgba(15,23,42,0.04)] flex-col">
            <div className="h-[48px] px-4 border-b border-white/70 flex items-center justify-between flex-shrink-0">
              <span className="text-[13.5px] font-semibold text-[#1D2129]">我的认知网络</span>
              <div className="flex items-center gap-2">
                {/* Search (moved from right panel) */}
                <div className="hidden md:flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-2.5 py-1.5">
                  <i className="ri-search-line text-[14px] text-[#6B7280]"></i>
                  <input
                    type="text"
                    value={searchVal}
                    onChange={(e) => setSearchVal(e.target.value)}
                    placeholder="搜索节点或概念"
                    className="w-[180px] text-[12.5px] text-[#1D2129] placeholder-[#C2C4CC] outline-none bg-transparent"
                  />
                  {searchVal && (
                    <button onClick={() => setSearchVal("")} className="text-[#9699A6] hover:text-[#646479] cursor-pointer">
                      <i className="ri-close-line text-[13px]"></i>
                    </button>
                  )}
                </div>
                <button onClick={() => graphRef.current?.reset()} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#646479] cursor-pointer" title="重置">
                  <i className="ri-refresh-line text-[14px]"></i>
                </button>
                <button onClick={() => graphRef.current?.zoomIn()} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#646479] cursor-pointer" title="放大">
                  <i className="ri-add-line text-[14px]"></i>
                </button>
                <button onClick={() => graphRef.current?.zoomOut()} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#646479] cursor-pointer" title="缩小">
                  <i className="ri-subtract-line text-[14px]"></i>
                </button>
              </div>
            </div>

            {/* Graph canvas */}
            <div ref={graphWrapRef} className="relative flex-1 min-h-0">
              <CogNetGraphCanvas
                ref={graphRef}
                selectedNode={selectedNode}
                onSelectNode={(id) => {
                  setSelectedNode(id);
                  setDetailOpen(!!id);
                }}
                compareMode={false}
                showEdges={showEdges}
                smartLayout={smartLayout}
                activeDomains={domainFilter}
                visibleMainNodeIds={computeVariant.my.mainIds}
                visibleLeafNodeIds={computeVariant.my.leafIds}
                commonLabels={compareStarted ? computeVariant.commonLabels : undefined}
                commonColorMap={computeVariant.commonColorMap}
                theme={darkMode ? "dark" : "light"}
              />

              {/* Mini knowledge domains card (top-left of my graph) */}
              <div className="absolute left-3 top-3 z-20">
                <div className="w-[220px] rounded-2xl border border-[#E5E7EB] bg-white/90 backdrop-blur">
                  <div className="px-3 pt-3 pb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#111827] leading-tight">知识领域</div>
                      <div className="text-[10.5px] text-[#8B8FA8] mt-0.5 leading-tight truncate">
                        {domainFilter.length ? `已选 ${domainFilter.length} 个` : "全部显示"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setShowEdges((v) => !v)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] ${showEdges ? "text-[#111827]" : "text-[#9CA3AF]"}`}
                        title="显示连接线"
                      >
                        <i className="ri-links-line text-[14px]"></i>
                      </button>
                      <button
                        onClick={() => setSmartLayout((v) => !v)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] ${smartLayout ? "text-[#111827]" : "text-[#9CA3AF]"}`}
                        title="智能布局"
                      >
                        <i className="ri-magic-line text-[14px]"></i>
                      </button>
                      <button
                        onClick={() => setDomainsCollapsed((v) => !v)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#646479]"
                        title={domainsCollapsed ? "展开" : "收起"}
                      >
                        <i className={`${domainsCollapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} text-[16px]`}></i>
                      </button>
                    </div>
                  </div>

                  {!domainsCollapsed && (
                    <div className="px-3 pb-3">
                      <div className="max-h-[168px] overflow-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <div className="grid grid-cols-1 gap-1.5">
                          {domainList.map((d) => {
                            const active = domainFilter.includes(d.name);
                            const muted = domainFilter.length > 0 && !active;
                            return (
                              <button
                                key={d.name}
                                onClick={() =>
                                  setDomainFilter((prev) => (prev.includes(d.name) ? prev.filter((n) => n !== d.name) : [...prev, d.name]))
                                }
                                className={`flex items-center justify-between rounded-xl px-2.5 py-2 text-left transition-colors ${
                                  active ? "bg-[#5E6AD2]" : "hover:bg-[#F9FAFB]"
                                } ${muted ? "opacity-60" : "opacity-100"}`}
                              >
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                  <span className={`text-[11.5px] truncate ${active ? "text-white" : "text-[#646479]"}`}>{d.name}</span>
                                </div>
                                <span className={`text-[11.5px] font-medium flex-shrink-0 ml-2 ${active ? "text-white" : "text-[#111827]"}`}>
                                  {d.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Node detail popup moved to right panel */}
            </div>
          </div>

          {compareStarted && (
            <div className="flex flex-1 min-w-0 h-full min-h-0 bg-white border border-white/70 rounded-3xl overflow-hidden shadow-[0_8px_24px_rgba(15,23,42,0.04)] flex-col">
              <div className="h-[48px] px-4 border-b border-white/70 flex items-center justify-between flex-shrink-0">
                <span className="text-[13.5px] font-semibold text-[#1D2129]">对方认知网络</span>
                <button onClick={() => { setCompareStarted(false); setCompareResult(null); setCompareSelectedNode(null); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#9699A6] cursor-pointer">
                  <i className="ri-close-line text-[15px]"></i>
                </button>
              </div>
              <div className="relative flex-1 min-h-0">
                <CogNetGraphCanvas
                  ref={compareGraphRef}
                  selectedNode={compareSelectedNode}
                  onSelectNode={setCompareSelectedNode}
                  compareMode={true}
                      showEdges={showEdges}
                  smartLayout={smartLayout}
                  activeDomains={compareDomainFilter}
                  visibleMainNodeIds={searchConfig ? searchConfig.other.main : undefined}
                  visibleLeafNodeIds={searchConfig ? searchConfig.other.leaf : undefined}
                  commonLabels={computeVariant.commonLabels}
                  commonColorMap={computeVariant.commonColorMap}
                  theme={darkMode ? "dark" : "light"}
                />

                {/* Mini knowledge domains card (top-left of compare graph) */}
                <div className="absolute left-3 top-3 z-20">
                  <div className="w-[220px] rounded-2xl border border-[#E5E7EB] bg-white/90 backdrop-blur">
                    <div className="px-3 pt-3 pb-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-[#111827] leading-tight">知识领域</div>
                        <div className="text-[10.5px] text-[#8B8FA8] mt-0.5 leading-tight truncate">
                          {compareDomainFilter.length ? `已选 ${compareDomainFilter.length} 个` : "全部显示"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setShowEdges((v) => !v)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] ${showEdges ? "text-[#111827]" : "text-[#9CA3AF]"}`}
                          title="显示连接线"
                        >
                          <i className="ri-links-line text-[14px]"></i>
                        </button>
                        <button
                          onClick={() => setSmartLayout((v) => !v)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] ${smartLayout ? "text-[#111827]" : "text-[#9CA3AF]"}`}
                          title="智能布局"
                        >
                          <i className="ri-magic-line text-[14px]"></i>
                        </button>
                        <button
                          onClick={() => setCompareDomainsCollapsed((v) => !v)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#646479]"
                          title={compareDomainsCollapsed ? "展开" : "收起"}
                        >
                          <i className={`${compareDomainsCollapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} text-[16px]`}></i>
                        </button>
                      </div>
                    </div>

                    {!compareDomainsCollapsed && (
                      <div className="px-3 pb-3">
                        <div className="max-h-[168px] overflow-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                          <div className="grid grid-cols-1 gap-1.5">
                            {compareDomainList.map((d) => {
                              const active = compareDomainFilter.includes(d.name);
                              const muted = compareDomainFilter.length > 0 && !active;
                              return (
                                <button
                                  key={d.name}
                                  onClick={() =>
                                    setCompareDomainFilter((prev) => (prev.includes(d.name) ? prev.filter((n) => n !== d.name) : [...prev, d.name]))
                                  }
                                  className={`flex items-center justify-between rounded-xl px-2.5 py-2 text-left transition-colors ${
                                    active ? "bg-[#5E6AD2]" : "hover:bg-[#F9FAFB]"
                                  } ${muted ? "opacity-60" : "opacity-100"}`}
                                >
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                    <span className={`text-[11.5px] truncate ${active ? "text-white" : "text-[#646479]"}`}>{d.name}</span>
                                  </div>
                                  <span className={`text-[11.5px] font-medium flex-shrink-0 ml-2 ${active ? "text-white" : "text-[#111827]"}`}>
                                    {d.count}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Right panel — hidden until node click / feature panel open */}
          {rightPanelOpen && (
            <>
              {activePanel ? (
                /* ===== Feature panel overlay ===== */
                <div className="w-[260px] flex-shrink-0 bg-white border border-white/70 rounded-2xl flex flex-col overflow-hidden shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
                  {/* Panel header */}
                  <div className="h-[48px] px-4 border-b border-white/70 flex items-center justify-between flex-shrink-0">
                    <span className="text-[13.5px] font-semibold text-[#1D2129]">
                      {activePanel === "timeline" && "时间轴"}
                      {activePanel === "updates" && "更新记录"}
                      {activePanel === "compare" && "链接对比"}
                      {activePanel === "compare-records" && "对比记录"}
                    </span>
                    <button
                      onClick={() => {
                        setActivePanel(null);
                        setDetailOpen(false);
                        setSelectedNode(null);
                      }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#9699A6] cursor-pointer"
                      title="关闭"
                    >
                      <i className="ri-close-line text-[15px]"></i>
                    </button>
                  </div>

                  {/* Panel content */}
                  <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden">

                {/* ===== TIMELINE PANEL ===== */}
                {activePanel === "timeline" && (
                  <div className="px-4 py-4">
                    <p className="text-[12px] text-[#8B8FA8] mb-4">认知网络随时间的演变历程，点击节点可快照回溯。</p>
                    <div className="relative">
                      {/* Vertical line */}
                      <div className="absolute left-[15px] top-0 bottom-0 w-px bg-[#E5E6EB]"></div>
                      <div className="flex flex-col gap-0">
                        {timelineData.map((item, idx) => (
                          <div key={item.date} className="flex gap-3 pb-5 relative">
                            {/* Dot */}
                            <div
                              className="w-[30px] flex-shrink-0 flex items-start justify-center pt-0.5 relative z-10"
                            >
                              <div
                                className="w-[10px] h-[10px] rounded-full border-2 border-white mt-1"
                                style={{ backgroundColor: item.color, boxShadow: `0 0 0 2px ${item.color}30` }}
                              ></div>
                            </div>
                            {/* Content */}
                            <div
                              className={`flex-1 bg-[#F9FAFB] border rounded-xl p-3 cursor-pointer transition-colors group ${
                                activeSnapshotDate === item.date ? "border-[#111827]" : "border-[#E5E6EB] hover:border-[#C8CACD]"
                              }`}
                              onClick={() => {
                                setActiveSnapshotDate(item.date);
                                setSelectedNode(null);
                                graphRef.current?.reset();
                              }}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[12px] font-semibold text-[#1D2129]">{item.date}</span>
                                {item.label && (
                                  <span className="text-[10.5px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${item.color}15`, color: item.color }}>{item.label}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-[11.5px] text-[#646479] mb-2">
                                <span className="flex items-center gap-1">
                                  <i className="ri-node-tree text-[11px]"></i>{item.nodes} 节点
                                </span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-links-line text-[11px]"></i>{item.edges} 关联
                                </span>
                                {idx > 0 && (
                                  <span className="text-[#111827] flex items-center gap-0.5">
                                    <i className="ri-arrow-up-line text-[11px]"></i>
                                    +{item.nodes - timelineData[idx + 1 < timelineData.length ? idx + 1 : idx].nodes}
                                  </span>
                                )}
                              </div>
                              {item.newNodes.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {item.newNodes.map((n) => (
                                    <span key={n} className="text-[10.5px] px-1.5 py-0.5 bg-white border border-[#E5E6EB] text-[#646479] rounded-full">{n}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== UPDATE RECORDS PANEL ===== */}
                {activePanel === "updates" && (
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[12px] text-[#8B8FA8]">最近 30 天的知识图谱变更记录</p>
                      <span className="text-[11px] text-[#9699A6] bg-[#F0F1F4] px-2 py-0.5 rounded-full">{updateRecords.length} 条</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {updateRecords.map((rec) => (
                        <div key={rec.id} className="flex gap-2.5 p-3 bg-[#F9FAFB] border border-[#E5E6EB] rounded-xl hover:border-[#C8CACD] cursor-pointer transition-colors">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ background: rec.bg }}>
                            <i className={`${rec.icon} text-[14px]`} style={{ color: rec.color }}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12.5px] font-medium text-[#1D2129] leading-snug">{rec.title}</p>
                            <p className="text-[11px] text-[#8B8FA8] mt-0.5 leading-relaxed">{rec.detail}</p>
                            <p className="text-[10.5px] text-[#C2C4CC] mt-1">{rec.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== COMPARE PANEL ===== */}
                {activePanel === "compare" && (
                  <div className="px-4 py-4">
                    <p className="text-[12px] text-[#8B8FA8] mb-4">输入他人的分享链接后，下面会并排展示你的网络和对方网络，并给出差异摘要。</p>

                    <div className="bg-[#F9FAFB] border border-[#E5E6EB] rounded-xl p-4 mb-4">
                      <div className="flex flex-col gap-3">
                        <div>
                          <label className="text-[11.5px] font-medium text-[#646479] mb-1.5 block">对方分享链接</label>
                          <input
                            type="text"
                            value={compareLink}
                            onChange={(e) => { setCompareLink(e.target.value); setCompareResult(null); }}
                            placeholder="例如：cognet.ai/u/physicist/305"
                            className="w-full border border-[#E5E6EB] rounded-lg px-3 py-2 text-[13px] text-[#1D2129] outline-none focus:border-[#111827] bg-white cursor-text"
                          />
                        </div>
                        <button
                          onClick={handleCompare}
                          className="w-full flex items-center justify-center gap-2 text-[13px] font-medium text-white bg-[#111827] hover:bg-black py-2.5 rounded-xl transition-colors cursor-pointer"
                        >
                          <i className="ri-scales-3-line text-[13px]"></i>
                          开始对比分析
                        </button>
                      </div>
                    </div>

                    {compareResult && (
                      <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-2">
                          {compareSummary.map((item) => (
                            <div key={item.label} className="rounded-xl border border-[#E5E6EB] bg-white p-3 text-center">
                              <p className="text-[16px] font-bold text-[#111827]">{item.value}</p>
                              <p className="text-[10.5px] text-[#8B8FA8] mt-0.5">{item.label}</p>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-xl border border-[#E5E6EB] bg-white p-3.5">
                            <p className="text-[12px] font-semibold text-[#111827] mb-2">我的认知网络</p>
                            <div className="space-y-2 text-[12px] text-[#4B5563]">
                              <p>节点：{compareResult.myNodes}</p>
                              <p>关联：{compareResult.myEdges}</p>
                              <p>仅我拥有：{compareResult.myOnlyNodes}</p>
                            </div>
                          </div>
                          <div className="rounded-xl border border-[#E5E6EB] bg-white p-3.5">
                            <p className="text-[12px] font-semibold text-[#111827] mb-2">对方认知网络</p>
                            <div className="space-y-2 text-[12px] text-[#4B5563]">
                              <p>节点：{compareResult.otherNodes}</p>
                              <p>关联：{compareResult.otherEdges}</p>
                              <p>仅对方有：{compareResult.otherOnlyNodes}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#F5F5F5] border border-[#E5E7EB] rounded-xl p-3 flex items-start gap-2">
                          <i className="ri-sparkling-2-line text-[13px] text-[#111827] mt-0.5 flex-shrink-0"></i>
                          <p className="text-[12px] text-[#4B5563] leading-relaxed">{compareResult.note}</p>
                        </div>

                        <button
                          onClick={() => setMissingModalOpen(true)}
                          className="w-full text-[12.5px] text-[#111827] bg-white border border-[#E5E6EB] hover:border-[#C8CACD] py-2 rounded-xl transition-colors cursor-pointer"
                        >
                          提取我缺少的节点
                        </button>

                        <button
                          onClick={handleSaveCompare}
                          className="w-full text-[12.5px] text-[#646479] hover:text-[#1D2129] border border-[#E5E6EB] hover:border-[#C8CACD] py-2 rounded-xl transition-colors cursor-pointer"
                        >
                          保存到对比记录
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ===== COMPARE RECORDS PANEL ===== */}
                {activePanel === "compare-records" && (
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[12px] text-[#8B8FA8]">历史保存的链接对比记录</p>
                      <button
                        onClick={() => setActivePanel("compare")}
                        className="flex items-center gap-1 text-[11.5px] text-[#111827] hover:underline cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-add-line text-[12px]"></i>新建对比
                      </button>
                    </div>
                    <div className="flex flex-col gap-3">
                      {compareRecords.map((rec) => (
                        <div key={rec.id} className="bg-[#F9FAFB] border border-[#E5E6EB] rounded-xl p-3.5 hover:border-[#C8CACD] cursor-pointer transition-colors group" onClick={() => { setCompareLink(rec.targetLink); setCompareResult(rec); setActivePanel("compare"); }}>
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-[12px] font-medium text-[#1D2129] bg-[#F0F1F4] px-2 py-0.5 rounded-lg truncate max-w-[220px]">{rec.targetLink}</span>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-2.5">
                            <div className="rounded-lg bg-white border border-[#E5E6EB] p-2 text-center">
                              <p className="text-[13px] font-bold text-[#111827]">{rec.commonNodes}</p>
                              <p className="text-[10px] text-[#8B8FA8]">共同节点</p>
                            </div>
                            <div className="rounded-lg bg-white border border-[#E5E6EB] p-2 text-center">
                              <p className="text-[13px] font-bold text-[#111827]">{rec.myOnlyNodes}</p>
                              <p className="text-[10px] text-[#8B8FA8]">仅我拥有</p>
                            </div>
                            <div className="rounded-lg bg-white border border-[#E5E6EB] p-2 text-center">
                              <p className="text-[13px] font-bold text-[#111827]">{rec.otherOnlyNodes}</p>
                              <p className="text-[10px] text-[#8B8FA8]">仅对方有</p>
                            </div>
                          </div>

                          <p className="text-[11.5px] text-[#646479] leading-relaxed line-clamp-2">{rec.note}</p>
                          <p className="text-[10.5px] text-[#C2C4CC] mt-2">{rec.time} 保存</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                  </div>
                </div>
              ) : (
                /* ===== Node detail side panel ===== */
                <div className="w-[320px] flex-shrink-0 bg-white border border-[#E5E7EB] rounded-2xl flex flex-col overflow-hidden shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
                  <div className="h-[48px] px-4 border-b border-[#F0F1F4] flex items-center justify-between flex-shrink-0">
                    <span className="text-[13.5px] font-semibold text-[#1D2129]">节点详情</span>
                    <button
                      onClick={() => {
                        setActivePanel(null);
                        setDetailOpen(false);
                        setSelectedNode(null);
                      }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#9699A6] cursor-pointer"
                      title="关闭"
                    >
                      <i className="ri-close-line text-[15px]"></i>
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden">
                    <div className="p-4">
                      {selectedDetail && (
                        <>
                          <div className="text-[13px] font-semibold text-[#111827]">{selectedDetail.title}</div>
                          <div className="text-[11px] text-[#8B8FA8] mt-1">{selectedNode}</div>
                          {selectedDetail.nodeKind === "leaf" && selectedDetail.parentTitle && (
                            <div className="mt-2 text-[11px] text-[#6B7280]">
                              上级：<span className="font-medium text-[#111827]">{selectedDetail.parentTitle}</span>
                            </div>
                          )}
                          <div className="mt-3 text-[11.5px] text-[#4B5563] leading-relaxed">{selectedDetail.meaning}</div>

                          {/* Sources */}
                          <div className="mt-4">
                            <div className="text-[11px] font-semibold text-[#111827] mb-2">
                              {selectedDetail.nodeKind === "leaf" ? "本条来源" : "来源（汇总）"}
                            </div>
                            <div className="flex flex-col gap-2">
                              {(selectedDetail.nodeKind === "main" ? selectedDetail.aggregatedSources : selectedDetail.sources).map((s, idx) => (
                                <a
                                  key={idx}
                                  href={s.href}
                                  className="group flex items-start gap-2 rounded-xl border border-[#E5E6EB] bg-white px-2.5 py-2 hover:border-[#C8CACD] hover:bg-[#F9FAFB] transition-colors"
                                >
                                  <div
                                    className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      s.kind === "qa" ? "bg-[#EEF2FF] text-[#3D69E8]" : "bg-[#ECFDF5] text-[#10B981]"
                                    }`}
                                  >
                                    <i className={`${s.kind === "qa" ? "ri-question-answer-line" : "ri-file-list-2-line"} text-[14px]`}></i>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[11.5px] text-[#1D2129] leading-snug line-clamp-2">{s.title}</div>
                                    <div className="text-[10.5px] text-[#8B8FA8] mt-0.5 truncate">{s.href}</div>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>

                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Click outside to close share popup */}
      {showShare && (
        <div className="fixed inset-0 z-40" onClick={() => setShowShare(false)}></div>
      )}

      {/* Missing nodes modal */}
      {missingModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onClick={() => setMissingModalOpen(false)}>
          <div className="w-full max-w-[520px] rounded-2xl bg-white border border-[#E5E6EB] overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#F0F1F4] flex items-center justify-between">
              <div>
                <div className="text-[13.5px] font-semibold text-[#1D2129]">我缺少的节点</div>
                <div className="text-[11.5px] text-[#8B8FA8] mt-0.5">基于对比结果提取（仅对方有）</div>
              </div>
              <button onClick={() => setMissingModalOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F6F9] text-[#9699A6] cursor-pointer">
                <i className="ri-close-line text-[15px]"></i>
              </button>
            </div>

            <div className="px-4 py-3 flex items-center justify-between gap-2">
              <div className="text-[12px] text-[#4B5563]">共 {computeVariant.otherOnlyLabels.length} 个</div>
              <div className="flex items-center gap-2">
                <button onClick={async () => { try { await navigator.clipboard.writeText(computeVariant.otherOnlyLabels.join("\n")); } catch {} }} className="text-[12px] font-medium text-[#111827] border border-[#E5E6EB] hover:border-[#C8CACD] px-3 py-1.5 rounded-lg">
                  复制
                </button>
                <button onClick={() => exportMissing("txt")} className="text-[12px] font-medium text-[#111827] border border-[#E5E6EB] hover:border-[#C8CACD] px-3 py-1.5 rounded-lg">
                  导出 TXT
                </button>
                <button onClick={() => exportMissing("csv")} className="text-[12px] font-medium text-white bg-[#111827] hover:bg-black px-3 py-1.5 rounded-lg">
                  导出 CSV
                </button>
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto px-4 pb-4">
              <div className="grid grid-cols-2 gap-2">
                {computeVariant.otherOnlyLabels.map((name) => (
                  <div key={name} className="rounded-xl border border-[#E5E6EB] bg-[#F9FAFB] px-3 py-2 text-[12px] text-[#1D2129]">
                    {name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
